import { careerEvolution } from '@/mocks/portfolio';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import SlotText from '@/components/feature/SlotText';

export default function Career() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section id="career" className="py-20 md:py-28">
      <div className="mx-auto max-w-[1280px] px-6 md:px-10">
        {/* Header */}
        <div
          ref={ref}
          className={`mb-14 transition-all duration-1000 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
            <span className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
              Career Evolution
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-6">
              <h2 className="font-heading fw-bold text-[clamp(32px,4.5vw,60px)] text-foreground-950 leading-[0.93] tracking-[-0.04em]">
                <SlotText text="The scope" as="span" />
                <br />
                <SlotText text="expanded." as="span" className="marker-yellow" color="oklch(0.09 0.01 70)" scrambleColor="rainbow" />
                <br />
                <SlotText text="The core did not." as="span" className="text-foreground-500 fw-medium" />
              </h2>
            </div>
            <div className="md:col-span-5 md:col-start-8 flex items-end">
              <p className="text-foreground-700 text-base leading-relaxed">
                人の認知・行動・環境を捉えるデザインを起点に、表現 → コミュニケーション → ブランド → CX → 事業へと、扱う課題のレイヤーが段階的に拡張してきました。バラバラな職歴ではなく、一本の軸で貫かれたキャリアです。
              </p>
            </div>
          </div>
        </div>

        {/* Timeline — horizontal steps */}
        <div
          className={`relative transition-all duration-1000 delay-200 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Connector line */}
          <div className="hidden md:block absolute top-6 left-0 right-0 h-px bg-background-300 z-0" />

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-4">
            {careerEvolution.map((c, idx) => (
              <div key={c.phase} className="relative group cursor-default transition-transform duration-500 ease-out hover:-translate-y-1 hover:scale-[1.04]">
                {/* Phase badge */}
                <div className="flex md:flex-col items-start gap-4 md:gap-0">
                  <div
                    className={`w-12 h-12 flex-shrink-0 flex items-center justify-center z-10 relative transition-colors duration-300 ${
                      c.phase === 'NOW'
                        ? 'bg-primary-500 text-foreground-950'
                        : c.phase === 'EVOLVE'
                        ? 'bg-foreground-950 text-background-50 group-hover:bg-primary-500 group-hover:text-foreground-950'
                        : 'bg-background-50 border border-background-400 text-foreground-700 group-hover:bg-primary-500 group-hover:border-primary-500 group-hover:text-foreground-950'
                    }`}
                  >
                    <span className="font-label text-[9px] tracking-[0.08em] uppercase">{idx + 1}</span>
                  </div>

                  <div className="md:mt-6">
                    <p
                      className={`font-label text-[11px] tracking-[0.12em] uppercase mb-1 ${
                        c.phase === 'NOW'
                          ? 'text-primary-500'
                          : c.phase === 'EVOLVE'
                          ? 'text-foreground-950'
                          : 'text-foreground-500'
                      }`}
                    >
                      {c.phase}
                    </p>
                    <p className="font-heading text-lg md:text-xl text-foreground-950 leading-tight tracking-[-0.02em] mb-1">
                      {c.sub}
                    </p>
                    <p className="font-label text-[10px] tracking-[0.06em] text-foreground-500 mb-1">
                      {c.years}
                    </p>
                    <p className="font-label text-[10px] tracking-[0.06em] text-foreground-700 mb-3">
                      {c.company}
                    </p>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      {c.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Core statement */}
        <div
          className={`mt-14 md:mt-20 border border-background-300 bg-background-50 p-8 md:p-12 transition-all duration-1000 delay-300 ${revealed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-5">
              <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500 mb-4">
                The Core
              </p>
              <p className="font-heading text-2xl md:text-3xl text-foreground-950 leading-tight tracking-[-0.02em]">
                人がどう見て、
                <br />
                理解し、<SlotText text="動くか。" as="span" className="text-accent-600" scrambleColor="oklch(0.4 0.11 135)" />
              </p>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <p className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
                扱う領域は拡張しても、中心にあるのは常に「人の認知・行動・体験」を設計すること。空間・サイン・認知・視認性・可読性・行動への関心が、現在のCX / Brand Strategy / Communication Designの思考の原点になっています。
              </p>
              <p className="font-label text-[10px] tracking-[0.12em] uppercase text-foreground-500">
                20+ Years · 5 Companies · Spatial → Retail → Advertising → CX → Business
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}